// BUILD-049: React binding for the My Work "HR tasks" section (hrAssignedTasksController.ts holds the logic and its tests).
// One controller per host. It loads on mount, re-reads when the tab becomes visible again, and clears and reloads on a Caller change (the pilot persona switcher
// or another tab switching the server-held persona) while discarding any answer still in flight for the previous Caller.
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { getMyAssignedHrTasks, progressMyAssignedHrTask } from '../../api/client';
import { onCallerChanged, workApi } from '../../api/workShared';
import { createHrTasksController } from './hrAssignedTasksController';
import type { HrTasksController } from './hrAssignedTasksController';
import type { HrTasksState } from './hrAssignedTasksModel';

export function useHrAssignedTasks(): { state: HrTasksState; controller: HrTasksController } {
  const ref = useRef<HrTasksController | null>(null);
  if (ref.current === null) {
    ref.current = createHrTasksController({
      fetchPage: (params) => getMyAssignedHrTasks(params),
      progress: (id, input) => progressMyAssignedHrTask(id, input),
      getEpoch: () => workApi.getCallerEpoch(),
    });
  }
  const controller = ref.current;
  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  useEffect(() => {
    void controller.load();
    const offCaller = onCallerChanged(() => { void controller.callerChanged(); });
    const onVisible = (): void => { if (document.visibilityState === 'visible') void controller.refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { offCaller(); document.removeEventListener('visibilitychange', onVisible); };
  }, [controller]);
  return { state, controller };
}
